/**
 * Pure view model of the snapshots admin screen (И5, task T.C4b) — the journal
 * table, the source-freshness badges and the upload affordance.
 *
 * Framework-free on purpose (the `unit` Playwright project runs in plain Node):
 * type-only imports of the api layer, so nothing here drags `import.meta.env`
 * into the test process.
 *
 * Contract-first: the shapes come from #19/#20 (`api/snapshots.ts`), which the
 * api does not serve yet (T.B4) — the page is built against the frozen contract
 * and the e2e stubs it, so the T.B4 merge is a stub deletion, not a rewrite.
 */

import type { SnapshotApplyState, SnapshotDto, SnapshotKind, SnapshotListDto, SnapshotSource, SourcesHealthDto } from '../../api/snapshots';

import { formatDateTimeMsk } from '../dashboard/format';
import { ageLabel } from './settings-model';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** The two ingest sources of the ЕПГУ/ЕВИ funnel (docs/05 §3). */
export function kindLabel(kind: SnapshotKind): string {
  return kind === 'EPGU' ? 'ЕПГУ' : 'ЕВИ';
}

export function sourceLabel(source: SnapshotSource): string {
  return source === 'MAILBOX' ? 'Почта' : 'Вручную';
}

/**
 * The three numbers a journal row shows. The wire carries `rowsTotal`,
 * `rowsAccepted` and `rowsRejected`; in `packages/ingest` `rowsRejected` IS the
 * quarantine bucket (rejected rows are kept as evidence with pguId null), so
 * "rejected" and "quarantined" are one number in v1 — the column says so.
 */
export function snapshotCounts(snapshot: SnapshotDto): {
  total: number;
  accepted: number;
  quarantined: number;
} {
  return {
    total: snapshot.rowsTotal,
    accepted: snapshot.rowsAccepted,
    quarantined: snapshot.rowsRejected,
  };
}

/** A row the ingest could not finish — the api answers it with 500 and a reason. */
export function ingestErrorOf(snapshot: SnapshotDto): string | null {
  return snapshot.ingestError;
}

// ---------------------------------------------------------------------------
// What the count columns mean (T.Z39, owner question on prod)
// ---------------------------------------------------------------------------

/**
 * «ЕПГУ: Всего 5504 / Принято 5504, а на дашборде ~4496 заявлений — почему?» —
 * answered where the two numbers meet, not left for the next chat.
 *
 * «Всего» counts the FILE's rows. A daily export re-sends the WHOLE list as of
 * its date (for ЕПГУ — the whole campaign roster: 1685 → 2540 → 3485 → 5504 over
 * four files), so it is not «new today». `applications` holds ONE row per unique
 * `pgu_id` (a person standing in two rows of the roster is one application), and
 * the dashboard's «Заявления» counts those — all six statuses, nothing excluded
 * (#11 has no default status filter). The page shows no second number here on
 * purpose: #20 carries no application total, and importing #3 into an ingest
 * ledger tab to duplicate the dashboard's card would be a second source of truth
 * for one number.
 */
export const JOURNAL_TOTAL_HINT =
  'Всего — строки выгрузки за дату: источник присылает весь список заново (ЕПГУ — весь реестр кампании), а не только новых. В системе заявление одно на уникальный pgu_id, поэтому строк в файле может быть больше, чем заявлений на дашборде.';

/**
 * «Принято» is the ingest's own bookkeeping, never the funnel's verdict: a row
 * was read without errors. The same row arriving again tomorrow (or the same
 * person twice inside one file) upserts the application it already has — it does
 * not create a second one.
 */
export const JOURNAL_ACCEPTED_HINT =
  'Принято — строки, принятые без ошибок. Повторные строки прошлых дней (и человек, стоящий в двух строках) новых заявлений не создают.';

const NO_HEALTH: SourcesHealthDto = {
  EPGU: { lastSnapshotAt: null },
  EVI: { lastSnapshotAt: null },
  FTP: { lastSyncAt: null },
};

/**
 * Unwraps the #20 answer. `{ data: {snapshots, sourcesHealth} }` is what the
 * contract sends; a bare `SnapshotListDto` (hand-rolled stub) and a bare array
 * (the docs' shorthand «[SnapshotDto]») unwrap too — and a shape nobody sent is
 * an EMPTY journal with empty health, never `undefined`, so the page cannot
 * mistake "unexpected payload" for "nothing ingested yet".
 *
 * Lives here (not in the api layer) because the api module value-imports
 * `client.ts` and this module must stay loadable in the plain-Node unit project.
 */
export function parseSnapshotList(body: unknown): SnapshotListDto {
  if (Array.isArray(body)) return { snapshots: body as SnapshotDto[], sourcesHealth: NO_HEALTH };
  if (typeof body !== 'object' || body === null) return { snapshots: [], sourcesHealth: NO_HEALTH };

  const record = body as Record<string, unknown>;
  const payload =
    record['data'] && typeof record['data'] === 'object' && !Array.isArray(record['data'])
      ? record['data']
      : record;
  if (Array.isArray(payload)) return { snapshots: payload as SnapshotDto[], sourcesHealth: NO_HEALTH };

  const source = payload as Record<string, unknown>;
  const snapshots = Array.isArray(source['snapshots']) ? (source['snapshots'] as SnapshotDto[]) : [];
  const sourcesHealth = { ...NO_HEALTH, ...(source['sourcesHealth'] as SourcesHealthDto | undefined) };
  // T.Z40 — the api's own stall threshold; absent (an older api) means the page
  // judges with its documented default.
  const applyStaleAfterMs =
    typeof source['applyStaleAfterMs'] === 'number' && Number.isFinite(source['applyStaleAfterMs'])
      ? source['applyStaleAfterMs']
      : undefined;
  return { snapshots, sourcesHealth, applyStaleAfterMs };
}

// ---------------------------------------------------------------------------
// Source freshness
// ---------------------------------------------------------------------------

/**
 * «Fresh» threshold of the badges. The contract does not carry one (the full
 * SystemStatus panel of #23 with its thresholds is a separate later task), so
 * this is a presentation-only reading: a source silent for a week is stale, a
 * source that never reported is down.
 */
export const SOURCE_STALE_MS = 7 * 24 * 60 * 60 * 1000;

export type HealthTone = 'ok' | 'warn' | 'bad';

export function healthTone(
  lastAt: string | null | undefined,
  now: number,
  staleAfterMs: number = SOURCE_STALE_MS,
): HealthTone {
  if (typeof lastAt !== 'string' || lastAt.trim() === '') return 'bad';
  const at = Date.parse(lastAt);
  if (Number.isNaN(at)) return 'bad';
  const age = now - at;
  if (age > staleAfterMs) return 'warn';
  if (age < 0) return 'bad'; // a timestamp from the future is not evidence
  return 'ok';
}

export interface SourceHealthRow {
  key: 'EPGU' | 'EVI' | 'FTP';
  label: string;
  /** ISO-8601 of the freshest evidence, or null when the source never reported. */
  lastAt: string | null;
  /** What the timestamp means for this source. */
  hint: string;
}

export function healthRows(sourcesHealth: {
  EPGU?: { lastSnapshotAt: string | null };
  EVI?: { lastSnapshotAt: string | null };
  FTP?: { lastSyncAt: string | null };
} = {}): SourceHealthRow[] {
  return [
    { key: 'EPGU', label: 'ЕПГУ', lastAt: sourcesHealth.EPGU?.lastSnapshotAt ?? null, hint: 'последний снапшот' },
    { key: 'EVI', label: 'ЕВИ', lastAt: sourcesHealth.EVI?.lastSnapshotAt ?? null, hint: 'последний снапшот' },
    { key: 'FTP', label: 'FTP (УНТИ)', lastAt: sourcesHealth.FTP?.lastSyncAt ?? null, hint: 'последняя синхронизация' },
  ];
}

// ---------------------------------------------------------------------------
// The upload
// ---------------------------------------------------------------------------

/** The extensions #19 accepts (`docs/05 §4`: .7z archive or plain .xlsx). */
export const UPLOAD_ACCEPT = '.7z,.xlsx';

export const UPLOAD_TYPE_ERROR = 'Файл должен быть .7z или .xlsx';

/** The extension check the api repeats on its side — here it saves a round trip. */
export function uploadTypeError(file: File | undefined | null): string | undefined {
  if (!file) return undefined;
  const name = file.name.toLowerCase();
  return name.endsWith('.7z') || name.endsWith('.xlsx') ? undefined : UPLOAD_TYPE_ERROR;
}

/** The one file a drop or a picker contributes (a folder drop contributes none). */
export function firstUploadFile(files: FileList | null | undefined): File | undefined {
  return files?.[0];
}

// ---------------------------------------------------------------------------
// The apply pipeline (ADDITIVE v1.8.6, T.Z40) — «Конвейер» per journal row
// ---------------------------------------------------------------------------

/** The cadence the tab polls at WHILE a run is unfinished, «Автообновление» off or not. */
export const PIPELINE_POLL_MS = 15_000;

/**
 * The stall fallback for an api that does not publish `applyStaleAfterMs` yet —
 * the documented default of `SNAPSHOT_APPLY_STALE_MS` (10 min).
 */
export const DEFAULT_APPLY_STALE_MS = 10 * 60 * 1000;

/**
 * How long a stall stays an alarm: past the documented snapshot freshness
 * horizon (`SNAPSHOT_STALE_MS`, the panel's own 24 h) an unapplied snapshot is
 * history — a pre-ledger row or a dead ingest — not a live run to escalate.
 */
export const APPLY_FRESH_MS = 24 * 60 * 60 * 1000;

/** Tolerant read of the apply state: absent, malformed or an unknown additive member → null. */
export function applyStateOf(snapshot: Pick<SnapshotDto, 'apply'> | undefined): SnapshotApplyState | null {
  const apply = snapshot?.apply;
  if (typeof apply !== 'object' || apply === null) return null;
  const { state } = apply as { state?: unknown };
  if (state !== 'QUEUED' && state !== 'DONE' && state !== 'FAILED') return null;
  return apply;
}

/** No apply state at all, or one still queued — «the run has not landed yet». */
export function applyIsUnfinished(state: SnapshotApplyState | null): boolean {
  return state === null || state.state === 'QUEUED';
}

/** The four badge tones of the column (T.Z38's system: green done, yellow waiting, red failed). */
export type ApplyTone = 'ok' | 'warn' | 'bad' | 'neutral';

export function applyTone(state: SnapshotApplyState | null): ApplyTone {
  if (state === null) return 'neutral';
  return state.state === 'DONE' ? 'ok' : state.state === 'QUEUED' ? 'warn' : 'bad';
}

/** The stalled-apply alarm of ONE row, or null while it is still within its window. */
export interface ApplyStall {
  /** How long the snapshot has been waiting, human (the same words the panel ages use). */
  label: string;
}

/**
 * «Применение задерживается» — a fresh snapshot whose apply has not landed within
 * the threshold. Unfinished means absent OR queued: the mailbox path writes the
 * snapshot and the apply in two steps, so a missing state is itself evidence that
 * the second step has not happened. Outside the freshness horizon it stops: an
 * unapplied snapshot of last week is a fact of the ledger, not a live alarm.
 */
export function applyStall(
  snapshot: Pick<SnapshotDto, 'receivedAt' | 'apply'>,
  staleAfterMs: number,
  now: number,
): ApplyStall | null {
  if (typeof staleAfterMs !== 'number' || !Number.isFinite(staleAfterMs) || staleAfterMs <= 0) return null;
  const received = Date.parse(snapshot.receivedAt);
  if (Number.isNaN(received)) return null;
  const age = now - received;
  if (age < staleAfterMs || age > APPLY_FRESH_MS) return null;
  if (!applyIsUnfinished(applyStateOf(snapshot))) return null;
  return { label: `Применение задерживается (${ageLabel(age)})` };
}

/** The hover story of the alarm — the state badge it replaces is still in the ledger. */
export const APPLY_STALL_HINT =
  'Снапшот получен, но применение к реестру не завершилось за отведённое время — проверьте, работает ли воркер';

/** True while at least one row may still land its apply and is young enough to watch. */
export function hasOpenApply(snapshots: Array<Pick<SnapshotDto, 'receivedAt' | 'apply'> | undefined>, now: number): boolean {
  return snapshots.some((snapshot) => {
    if (snapshot === undefined) return false;
    const received = Date.parse(snapshot.receivedAt);
    if (Number.isNaN(received)) return false;
    const age = now - received;
    return age <= APPLY_FRESH_MS && applyIsUnfinished(applyStateOf(snapshot));
  });
}

/** The `refetchInterval` callback the journal hook takes: the admin's cadence, plus the light pipeline poll. */
export function pipelinePollInterval(body: unknown, now: number = Date.now()): number | false {
  return hasOpenApply(parseSnapshotList(body).snapshots, now) ? PIPELINE_POLL_MS : false;
}

/**
 * The label of a pipeline state, MSK everywhere. There is deliberately no
 * «применяется»: the pipeline records no in-progress marker (the apply is one
 * atomic transaction), so QUEUED is the honest wording for «not finished, no
 * failure seen» — the worker may already be applying it.
 */
export function applyLabel(state: SnapshotApplyState): string {
  switch (state.state) {
    case 'QUEUED':
      return 'В очереди';
    case 'DONE':
      return `Готово ${formatDateTimeMsk(state.finishedAt)}`;
    case 'FAILED':
      return `Ошибка ${formatDateTimeMsk(state.lastAttemptAt)}`;
  }
}

/** The second line: what the state's own counters say, or null when the state carries none. */
export function applyDetail(state: SnapshotApplyState): string | null {
  const parts: string[] = [];
  if (state.state === 'QUEUED') {
    parts.push(`запрос от ${formatDateTimeMsk(state.lastAttemptAt)}`);
  }
  if (state.state === 'DONE') {
    if (typeof state.appliedRows === 'number') parts.push(`строк ${state.appliedRows}`);
    if (typeof state.createdApps === 'number') parts.push(`новых ${state.createdApps}`);
    if (typeof state.mergedApps === 'number') parts.push(`объединено ${state.mergedApps}`);
  }
  if (state.state === 'FAILED') {
    if (typeof state.attempts === 'number') parts.push(`попыток ${state.attempts}`);
    if (typeof state.errorCode === 'string' && state.errorCode.trim() !== '') parts.push(state.errorCode);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The hover story per state — the api cannot say it, the ledger's shape can. */
export const APPLY_HINTS = {
  QUEUED: 'Снапшот ждёт применения: воркер заберёт его на своём следующем такте — применение уже может идти',
  DONE: 'Снапшот применён к реестру заявлений (написан вместе с самим применением)',
  FAILED: 'Применение не удалось — воркер повторит попытку; причина ищите в журнале воркера',
} as const satisfies Record<SnapshotApplyState['state'], string>;

/** What «no state at all» means: nothing in the ledger proves an apply for this row. */
export const APPLY_NONE_HINT =
  'Нет записи о применении: разбор файла не дошёл до применения, снапшот пришёл до ведения журнала, или применение ещё не завершилось';

// ---------------------------------------------------------------------------
// The auto-refresh select («Автообновление»)
// ---------------------------------------------------------------------------

/**
 * The journal refetch cadence the admin picks. `0` is «выкл» — no timer at all,
 * TanStack's `refetchInterval: false` — because a poll nobody asked for is
 * exactly the sort of background noise this tab should not add to the api.
 */
export type RefreshIntervalMs = 0 | 30_000 | 60_000 | 300_000;

export interface RefreshOption {
  value: RefreshIntervalMs;
  /** The `<option>` label, RU, human cadence — never a bare number of ms. */
  label: string;
}

export const REFRESH_OPTIONS: RefreshOption[] = [
  { value: 0, label: 'выкл' },
  { value: 30_000, label: '30 сек' },
  { value: 60_000, label: '1 мин' },
  { value: 300_000, label: '5 мин' },
];

/** Default is OFF: an admin who wants a live view asks for it, per visit and per browser. */
export const DEFAULT_REFRESH_MS: RefreshIntervalMs = 0;

/** `localStorage` key. Not namespaced per tab — there is only one snapshots journal. */
export const REFRESH_STORAGE_KEY = 'cb-manager.snapshots.refreshMs';

/**
 * Anything the storage did not write, or wrote after the option set changed, is
 * OFF — never a guessed cadence. Fail-closed, same rule as `parseRole`.
 */
export function parseRefreshIntervalMs(value: unknown): RefreshIntervalMs {
  const option = REFRESH_OPTIONS.find((candidate) => candidate.value === value);
  return option?.value ?? DEFAULT_REFRESH_MS;
}

/** The minimal storage shape this needs — the unit project has no `window`. */
export type RefreshStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function loadRefreshIntervalMs(storage: RefreshStorage | null | undefined): RefreshIntervalMs {
  if (!storage) return DEFAULT_REFRESH_MS;
  let raw: string | null = null;
  try {
    raw = storage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return DEFAULT_REFRESH_MS; // private mode / disabled storage is not an error
  }
  if (raw === null) return DEFAULT_REFRESH_MS;
  const parsed = Number(raw);
  return parseRefreshIntervalMs(Number.isFinite(parsed) ? parsed : null);
}

export function saveRefreshIntervalMs(storage: RefreshStorage | null | undefined, value: RefreshIntervalMs): void {
  if (!storage) return;
  try {
    storage.setItem(REFRESH_STORAGE_KEY, String(value));
  } catch {
    // Quota / privacy mode: the select still works, the choice just does not persist.
  }
}

/** The label of a cadence, for the status line and the aria-live announcements. */
export function refreshLabel(value: RefreshIntervalMs): string {
  return REFRESH_OPTIONS.find((option) => option.value === value)?.label ?? 'выкл';
}
